import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Database, Play, ArrowRight, X, Loader2, CheckCircle2 } from 'lucide-react';
import { useTour } from '../contexts/TourContext';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * WelcomeOnboarding — the SINGLE entry point to all guided onboarding.
 *
 * Standing order from product owner: ONE tour, ONE optional demo-data load,
 * ONE skip. No competing mini-tours, no overlapping experiences.
 *
 * Logic:
 *  - On first render after login, check `/api/onboarding/state`
 *  - If `!has_seen_welcome` → show this modal
 *  - User picks one of THREE paths:
 *      (a) "Show me with demo data"  → POST /api/onboarding/load-demo-data,
 *                                       then start the main guided tour.
 *      (b) "Just the tour, I'll add my own data" → start the main guided tour,
 *                                                   no demo data loaded.
 *      (c) "Skip — I'll explore on my own" → close the modal forever.
 *  - All three flip `has_seen_welcome=true` so this never reappears.
 *  - Available later in Settings → Help → "Replay onboarding".
 */
export function WelcomeOnboarding() {
  const { user } = useAuth();
  const { startTour } = useTour();
  const { refresh: refreshBrands } = useBrand();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [doneStep, setDoneStep] = useState(false);

  // ── Decide whether to show, once per logged-in session.
  useEffect(() => {
    if (!user) return;
    // Role-scoped: this modal is the SUBSCRIBER-ADMIN onboarding entrance only.
    // Employees see /welcome (WelcomeOrientationPage), country operators run
    // the CountryOperatorOnboardingPage wizard, and super_admin skips entirely.
    if (['super_admin', 'team_member', 'country_operator'].includes(user.role)) return;
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/onboarding/state`);
        if (active && !data.has_seen_welcome) {
          // Delay so we don't fight any auth redirect animation
          setTimeout(() => active && setOpen(true), 700);
        }
      } catch { /* ignore — backend not deployed yet */ }
    })();
    return () => { active = false; };
  }, [user]);

  const finishAndClose = async () => {
    try { await axios.post(`${API}/api/onboarding/mark-seen`); } catch {}
    setOpen(false);
  };

  const startWithDemoData = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/onboarding/load-demo-data`);
      setDoneStep(true);
      await refreshBrands();
      // Small pause so the user reads the "loaded" confirmation
      setTimeout(async () => {
        await finishAndClose();
        startTour();
      }, 1400);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load demo data — starting tour anyway.');
      await finishAndClose();
      startTour();
    } finally {
      setLoading(false);
    }
  };

  const startTourOnly = async () => {
    await finishAndClose();
    startTour();
  };

  const skipEverything = async () => {
    await finishAndClose();
    toast.success('You can replay the tour anytime from Settings → Help.', { duration: 5000 });
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) skipEverything(); }}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="welcome-onboarding">
        <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 p-6 text-white relative">
          <button
            onClick={skipEverything}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Close"
            data-testid="welcome-close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-100 mb-2">
            <Sparkles className="h-3.5 w-3.5" /> Welcome to Konnect
          </div>
          <h2 className="text-2xl font-bold leading-tight mb-1">
            Hi {user.full_name?.split(' ')[0] || 'there'} 👋
          </h2>
          <p className="text-sm text-emerald-50 leading-relaxed">
            Konnect can manage your whole marketing stack — content, ads, reputation, AI influencers — all from one dashboard. How would you like to start?
          </p>
        </div>

        {doneStep ? (
          <div className="p-8 text-center" data-testid="welcome-loaded-confirm">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <p className="font-semibold text-gray-900">Demo data loaded — 3 brands, 20 drafts, 50 reviews</p>
            <p className="text-sm text-gray-600 mt-1">Starting the tour in a moment…</p>
          </div>
        ) : (
          <div className="p-5 space-y-2.5">
            <button
              onClick={startWithDemoData}
              disabled={loading}
              className="w-full text-left p-4 border-2 border-emerald-200 hover:border-emerald-400 bg-emerald-50/40 hover:bg-emerald-50 rounded-xl transition group disabled:opacity-50"
              data-testid="welcome-with-demo"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                    Take a tour with sample data
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">RECOMMENDED</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Loads 3 demo brands + drafts + reviews so the tour feels real. You can wipe them with one click later.
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-emerald-500 mt-2 flex-shrink-0" />
              </div>
            </button>

            <button
              onClick={startTourOnly}
              disabled={loading}
              className="w-full text-left p-4 border border-gray-200 hover:border-gray-400 rounded-xl transition group disabled:opacity-50"
              data-testid="welcome-tour-only"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center flex-shrink-0">
                  <Play className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-sm">Quick tour only</div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Walk through the dashboard without demo data — I&apos;ll add my own as I go.
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-700 mt-2 flex-shrink-0" />
              </div>
            </button>

            <button
              onClick={skipEverything}
              disabled={loading}
              className="w-full text-center py-2.5 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50"
              data-testid="welcome-skip"
            >
              Skip — I&apos;ll explore on my own
            </button>
            <p className="text-[10px] text-center text-gray-400 px-4">
              You can replay this anytime from <strong>Settings → Help</strong>.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default WelcomeOnboarding;
